import React, { useEffect, useState } from 'react';
import { Button, Grid, makeStyles } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { useDispatch, useSelector } from 'react-redux';
import _isEmpty from 'lodash/isEmpty';
import _keyBy from 'lodash/keyBy';
import _map from 'lodash/map';
import _compact from 'lodash/compact';
import moment from 'moment';
import Modal from '../common/Modal';
import Steps from '../common/Steps';
import { CertInfo } from './CertInfo';
import { IssuerInfo } from './IssuerInflo';
import { JourneyList } from './JourneyList';
import { AddIssuer } from './AddIssuer';
import { ErrorToast, MultipleErrorToast, notifySuccess } from '../utils/Utils';
import service from '../../apis/service';
import { apiEndpoints } from '../../constants/apiEndpoints';
import {
  setCertSidebar,
  setSelectedCert,
  setSelectedCertInfoEdit,
  showProgress,
} from '../../redux/actions/DealershipActions';
import useSelectedCertInfo from './hooks/useSelectedCertInfo';

const useStyle = makeStyles({
  popupContainer: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
});

export function AddCertPopup({ isEdit, isOpenAddCert, setIsOpenAddCert }) {
  const classes = useStyle();
  const dispatch = useDispatch();
  const deptList = useSelector((state) => state.getAllDepartments.departments);
  const fetchSelectedCertInfo = useSelectedCertInfo();
  const [step, setStep] = useState(1);
  const [isAddIssuer, setIsAddIssuer] = useState(false);
  const [isEditIssuer, setIsEditIssuer] = useState(false);
  const [slectedIssuer, setSelectedIssuer] = useState(null);
  const [certImg, setCertImg] = useState(null);
  const [certUrl, setCertUrl] = useState('');
  const [thumbnailUrl, setThumbnailUrl] = useState('');
  const [distinctionImg, setDistinctionImg] = useState(null);
  const [thumbnailImg, setThumbnailImg] = useState(null);
  const [distinctionUrl, setDistinctionUrl] = useState('');
  const [videoFile, setVideoFile] = useState(null);
  const [videoUrl, setVideoUrl] = useState('');
  const [issuerName, setIssuerName] = useState('');
  const [issuerLogo, setIssuerLogo] = useState(null);
  const [issuerIcon, setIssuerIcon] = useState(null);
  const [logoUrl, stLogoUrl] = useState('');
  const [iconUrl, setIconUrl] = useState('');
  const [selectedDeptIds, setSelectedDeptIds] = useState([]);
  const [selectedCertIds, setSelectedCertIds] = useState([]);
  const [certList, setCertList] = useState([]);
  const [isAssignable, setIsAssignable] = useState(false);
  const [selectedRnp, setSelectedRnp] = useState(null);
  const [expDate, setExpDate] = useState(null);

  function generateCertificateCode() {
    const min = 1000;
    const max = 9999;
    const randomCode = +Math.floor(Math.random() * (max - min + 1)) + min;
    const code = `CC${randomCode.toString()}`;
    return code;
  }

  const [certInfo, setCertInfo] = useState({
    code: generateCertificateCode(),
    name: '',
    overview: '',
    duration: '',
    skillLevelId: '',
    issuerId: '',
    journeys: '',
    courseCode: '',
  });

  const selectedCertInfo = useSelector((state) => state.certificateData.selectedCertInfo);
  const selectedCertInfoEdit = useSelector((state) => state.certificateData.selectedCertInfoEdit);

  const fetchDataEdit = async (id) => {
    dispatch(showProgress(true));
    try {
      const res = await service.get(apiEndpoints.CERT_CERT_INFO_EDIT.replace('ID', id));
      dispatch(setSelectedCertInfoEdit(res));
    } catch (error) {
      ErrorToast(error);
    } finally {
      dispatch(showProgress(false));
    }
  };

  useEffect(() => {
    if (selectedCertInfo?.id) {
      fetchDataEdit(selectedCertInfo.id);
    }
  }, [selectedCertInfo]);

  useEffect(() => {
    if (isEdit && selectedCertInfoEdit.id && certList?.length && deptList?.length) {
      const temp = {
        code: selectedCertInfoEdit.code,
        name: selectedCertInfoEdit.name,
        overview: selectedCertInfoEdit.overview,
        duration: selectedCertInfoEdit.duration,
        skillLevelId: selectedCertInfoEdit.skill,
        issuerId: selectedCertInfoEdit.issuer,
        journeys: selectedCertInfoEdit.journeys || [],
        courseCode: selectedCertInfoEdit.courseCode || '',
      };
      setIsAssignable(selectedCertInfoEdit.isOnlyAssignable);
      setCertUrl(selectedCertInfoEdit.passingCertificationImage);
      setDistinctionUrl(selectedCertInfoEdit.distinctionCertificateImage);
      setThumbnailUrl(selectedCertInfoEdit.thumbnail);
      setVideoUrl(selectedCertInfoEdit.consentVideoLink || '');
      setCertInfo(temp);
      const rawExpiry = selectedCertInfoEdit.expiryDate;
      setExpDate(rawExpiry && moment(rawExpiry).isValid() ? rawExpiry : null);
      setSelectedDeptIds(
        selectedCertInfoEdit?.departments?.map((deptId) => deptList?.filter((dept) => dept?.value === deptId)[0]),
      );
      if (selectedCertInfoEdit?.preRequisiteIds?.length) {
        const certificateById = _keyBy(certList, 'value');
        const scert = _map(selectedCertInfoEdit?.preRequisiteIds, (id) => certificateById[id]);
        const sanitizedCerts = _compact(scert);
        setSelectedCertIds(sanitizedCerts);
      } else {
        setSelectedCertIds([]);
      }
    }
  }, [certList, deptList, isEdit, selectedCertInfoEdit]);

  function checkValidation() {
    const errors = [];
    let isValid = true;
    if (step === 1 && !(selectedCertInfoEdit?.editWarning && isEdit)) {
      if (certInfo?.code.trim() === '') {
        isValid = false;
        errors.push('Please enter code for certificate');
      }
      if (certInfo?.name.trim() === '') {
        isValid = false;
        errors.push('Please enter certificate title');
      }
      if (certInfo?.overview.trim() === '') {
        isValid = false;
        errors.push('Please enter certificate overview');
      }
      if (!certInfo.duration && !(selectedCertInfoEdit?.editWarning && isEdit)) {
        isValid = false;
        errors.push('Please enter certificate duration');
      }
      if (
        certInfo.duration &&
        Number.isNaN(Number(certInfo.duration)) &&
        !(selectedCertInfoEdit?.editWarning && isEdit)
      ) {
        isValid = false;
        errors.push('Certificate duration must be a number');
      }
      if (!certInfo.skillLevelId) {
        isValid = false;
        errors.push('Please select skill level');
      }

      if (selectedDeptIds?.length === 0) {
        isValid = false;
        errors.push('Please select a department');
      }
    }
    if (step === 1 && !isEdit) {
      if (!thumbnailImg) {
        isValid = false;
        errors.push('Please select a thumbnail');
      }
    }
    if (step === 1 && selectedCertInfoEdit?.editWarning && isEdit) {
      if (certInfo?.name.trim() === '') {
        isValid = false;
        errors.push('Please enter certificate title');
      }
      if (certInfo?.overview.trim() === '') {
        isValid = false;
        errors.push('Please enter certificate overview');
      }
      if (!certInfo.skillLevelId) {
        isValid = false;
        errors.push('Please select skill level');
      }

      if (selectedDeptIds?.length === 0) {
        isValid = false;
        errors.push('Please select a department');
      }
      if (selectedCertInfoEdit.duration !== certInfo.duration || selectedCertInfoEdit.code !== certInfo.code) {
        isValid = false;
        errors.push(selectedCertInfoEdit.editWarning);
        const temp = { ...certInfo };
        temp.duration = selectedCertInfoEdit.duration;
        temp.code = selectedCertInfoEdit.code;
        setCertInfo(temp);
        setCertUrl(selectedCertInfoEdit.passingCertificationImage);
        setDistinctionUrl(selectedCertInfoEdit.distinctionCertificateImage);
        setCertImg(null);
        setDistinctionImg(null);
        setThumbnailImg(null);
        setThumbnailUrl(selectedCertInfoEdit.thumbnail);
      }
    }
    if (step === 2 && !isAddIssuer && !certInfo.issuerId) {
      isValid = false;
      errors.push('Please select a issuer');
    }
    if (step === 2 && !selectedRnp && isAddIssuer) {
      isValid = false;
      errors.push('Please select a partner');
    }
    if (step === 2 && (isAddIssuer || isEditIssuer)) {
      if (!issuerName || issuerName.trim() === '') {
        isValid = false;
        errors.push('Please enter issuer name');
      }
      if (!issuerLogo && isAddIssuer) {
        isValid = false;
        errors.push('Please upload issuer logo');
      }
      if (!issuerIcon && isAddIssuer) {
        isValid = false;
        errors.push('Please upload issuer icon');
      }
    }
    if (step === 3 && certInfo.journeys.length === 0) {
      if (certInfo.journeys.length === 0) {
        isValid = false;
        errors.push('Please select a journey');
      }
    }

    if (errors.length !== 0) {
      MultipleErrorToast(errors);
    }
    return isValid;
  }

  const onNext = async () => {
    const isValid = checkValidation();
    if (!isValid) return;

    if (step < 3) {
      setStep((pre) => pre + 1);
    }

    if (step === 3 && !isEdit) {
      const data = new global.FormData();
      data.append('code', certInfo.code);
      data.append('name', certInfo.name);
      data.append('overview', certInfo.overview);
      data.append('duration', certInfo.duration);
      data.append('skillLevelId', certInfo.skillLevelId);
      data.append('issuerId', certInfo.issuerId);
      data.append('isOnlyAssignable', isAssignable);
      data.append('courseCode', certInfo.courseCode || '');
      selectedDeptIds?.forEach((obj) => {
        if (obj.value !== '*') {
          data.append('departments', obj.value);
        }
      });
      if (certImg) {
        data.append('passingCertificationImage', certImg);
      }
      if (distinctionImg) {
        data.append('distinctionCertificateImage', distinctionImg);
      }
      if (thumbnailImg) {
        data.append('thumbnail', thumbnailImg);
      }

      if (expDate && moment(expDate).isValid()) {
        data.append('expiryDate', moment(expDate).format('YYYY-MM-DD'));
      }

      certInfo.journeys.forEach((jid) => {
        data.append('journeys', jid);
      });

      if (selectedCertIds?.length) {
        selectedCertIds?.forEach((obj) => {
          if (obj.value !== '*') {
            data.append('preRequisiteIds', obj.value);
          }
        });
      }

      if (videoFile) {
        data.append('consentVideo', videoFile);
      } else if (videoUrl) {
        data.append('consentVideoLink', videoUrl);
      }

      dispatch(showProgress(true));
      try {
        await service.post(apiEndpoints.CERT_CREATE, data);
        notifySuccess('Certificate created successfully');
        handleCloseCertPopup();
        fetchSidebarList();
      } catch (error) {
        ErrorToast(error);
      }
      dispatch(showProgress(false));
    }

    if (step === 3 && isEdit) {
      const data = new global.FormData();
      data.append('code', certInfo.code);
      data.append('name', certInfo.name);
      data.append('overview', certInfo.overview);
      data.append('duration', certInfo.duration);
      data.append('skillLevelId', certInfo.skillLevelId);
      data.append('issuerId', certInfo.issuerId);
      data.append('isOnlyAssignable', isAssignable);
      data.append('courseCode', certInfo.courseCode || '');
      selectedDeptIds?.forEach((obj) => {
        if (obj.value !== '*') {
          data.append('departments', obj.value);
        }
      });
      if (certImg) {
        data.append('passingCertificationImage', certImg);
      } else if (certUrl !== '') {
        data.append('passingCertificationImageLink', certUrl);
      }
      if (distinctionImg) {
        data.append('distinctionCertificateImage', distinctionImg);
      } else if (distinctionUrl !== '') {
        data.append('distinctionCertificateImageLink', distinctionUrl);
      }
      if (thumbnailImg) {
        data.append('thumbnail', thumbnailImg);
      }

      if (expDate && moment(expDate).isValid()) {
        data.append('expiryDate', moment(expDate).format('YYYY-MM-DD'));
      }

      certInfo.journeys.forEach((jid) => {
        data.append('journeys', jid);
      });

      if (selectedCertIds?.length) {
        selectedCertIds?.forEach((obj) => {
          if (obj.value !== '*') {
            data.append('preRequisiteIds', obj.value);
          }
        });
      }

      if (videoFile) {
        data.append('consentVideo', videoFile);
      } else if (videoUrl) {
        data.append('consentVideoLink', videoUrl);
      }

      dispatch(showProgress(true));
      try {
        await service.post(apiEndpoints.CERT_UPDATE.replace('ID', selectedCertInfo.id), data);
        notifySuccess('Certificate updated successfully');
        handleCloseCertPopup();
        fetchSelectedCertInfo();
        fetchSidebarList(selectedCertInfo.id);
      } catch (error) {
        ErrorToast(error);
      }
      dispatch(showProgress(false));
    }
  };

  const handleCloseCertPopup = () => {
    setIsOpenAddCert(false);
    setCertList([]);
    setSelectedCertIds([]);
  };

  const onAddIssuer = async () => {
    const isValid = checkValidation();
    if (!isValid) return;

    dispatch(showProgress(true));
    const data = new global.FormData();
    data.append('name', issuerName);
    data.append('logo', issuerLogo);
    data.append('icon', issuerIcon);
    data.append('rockedNetworkPartnerId', selectedRnp);

    try {
      await service.post(apiEndpoints.CERT_CREATE_ISSUER, data);
      notifySuccess('Issuer created successfully');
      setIsAddIssuer(false);
      setIsEditIssuer(false);
    } catch (error) {
      ErrorToast(error);
    }

    dispatch(showProgress(false));
  };

  const onEditIssuer = async () => {
    const isValid = checkValidation();
    if (!isValid) return;

    dispatch(showProgress(true));
    const data = new global.FormData();
    data.append('name', issuerName);
    data.append('rockedNetworkPartnerId', selectedRnp);
    if (issuerLogo) {
      data.append('logo', issuerLogo);
    }
    if (issuerIcon) {
      data.append('icon', issuerIcon);
    }

    try {
      await service.post(apiEndpoints.CERT_ISSUER_UPDATE.replace('ID', slectedIssuer), data);
      notifySuccess('Issuer updated successfully');
      setIsAddIssuer(false);
      setIsEditIssuer(false);
    } catch (error) {
      ErrorToast(error);
    }

    dispatch(showProgress(false));
  };

  const onBack = () => {
    if (isAddIssuer || isEditIssuer) {
      setIsAddIssuer(false);
      setIsEditIssuer(false);
    } else {
      setStep((pre) => pre - 1);
    }
  };

  const fetchSidebarList = async (id) => {
    dispatch(showProgress(true));
    try {
      const res = await service.get(apiEndpoints.CERT_ALL_CERTS);
      const data = id ? res.filter((d) => d.id === id)[0] : res[0];
      dispatch(setCertSidebar(res));
      if (!_isEmpty(res)) {
        handleListItemClick(data);
      }
    } catch (error) {
      ErrorToast(error);
    }
    dispatch(showProgress(false));
  };

  const handleListItemClick = async (data = {}) => {
    dispatch(showProgress(true));

    if (!_isEmpty(data)) {
      dispatch(setSelectedCert(data));
    }
    dispatch(showProgress(false));
  };

  const getAllCerts = async () => {
    try {
      const res = (await service.get(apiEndpoints.CERT_ALL_CERTS)) || [];
      const filteredCerts =
        isEdit && selectedCertInfo?.id ? res.filter((item) => item.id !== selectedCertInfo.id) : res;

      const temp = filteredCerts.map((item) => ({ label: item.name, value: item.id }));
      setCertList(temp);
    } catch (error) {
      console.error('Error fetching certificates:', error);
    }
  };

  useEffect(() => {
    getAllCerts();
  }, []);

  return (
    <Modal isOpen={isOpenAddCert} height="84vh" width="820px">
      <Grid className={classes.popupContainer}>
        <Grid container direction="row" justifyContent="space-between" alignItems="flex-start">
          <Grid item>
            <Grid item style={{ fontWeight: '600', fontSize: '18px' }}>
              {isEdit ? 'Edit Certification' : 'Add Certification'}
            </Grid>
          </Grid>
          <Grid item>
            <CloseIcon onClick={handleCloseCertPopup} style={{ cursor: 'pointer' }} />
          </Grid>
        </Grid>
        <Steps step={step} stepInfo={['Certification Info', 'Issuer Info', 'Journeys']} />
        {step === 1 && (
          <CertInfo
            certInfo={certInfo}
            setCertInfo={setCertInfo}
            certImg={certImg}
            distinctionImg={distinctionImg}
            thumbnailImg={thumbnailImg}
            setCertImg={setCertImg}
            setDistinctionImg={setDistinctionImg}
            setThumbnailImg={setThumbnailImg}
            setSelectedDeptIds={setSelectedDeptIds}
            selectedDeptIds={selectedDeptIds}
            selectedCertIds={selectedCertIds}
            setSelectedCertIds={setSelectedCertIds}
            thumbnailUrl={thumbnailUrl}
            videoFile={videoFile}
            setVideoFile={setVideoFile}
            videoUrl={videoUrl}
            setVideoUrl={setVideoUrl}
            certUrl={certUrl}
            distinctionUrl={distinctionUrl}
            setCertUrl={setCertUrl}
            setDistinctionUrl={setDistinctionUrl}
            setThumbnailUrl={setThumbnailUrl}
            deptList={deptList}
            isEdit={isEdit}
            certList={certList}
            isAssignable={isAssignable}
            setIsAssignable={setIsAssignable}
            expDate={expDate}
            setExpDate={setExpDate}
          />
        )}
        {step === 2 && !(isAddIssuer || isEditIssuer) && (
          <IssuerInfo
            certInfo={certInfo}
            setCertInfo={setCertInfo}
            setIsAddIssuer={setIsAddIssuer}
            setIsEditIssuer={setIsEditIssuer}
            slectedIssuer={slectedIssuer}
            setSelectedIssuer={setSelectedIssuer}
            isEdit={isEdit}
          />
        )}
        {step === 2 && (isAddIssuer || isEditIssuer) && (
          <AddIssuer
            issuerName={issuerName}
            setIssuerName={setIssuerName}
            issuerLogo={issuerLogo}
            setIssuerLogo={setIssuerLogo}
            issuerIcon={issuerIcon}
            setIssuerIcon={setIssuerIcon}
            stLogoUrl={stLogoUrl}
            logoUrl={logoUrl}
            setIconUrl={setIconUrl}
            iconUrl={iconUrl}
            isEdit={isEditIssuer}
            issuerId={slectedIssuer}
            selectedRnp={selectedRnp}
            setSelectedRnp={setSelectedRnp}
          />
        )}
        {step === 3 && <JourneyList certInfo={certInfo} setCertInfo={setCertInfo} isEdit={isEdit} />}
        <Grid style={{ display: 'flex' }}>
          {step > 1 && (
            <Button onClick={onBack} variant="outlined">
              Back
            </Button>
          )}
          <Grid container direction="row" justifyContent="flex-end" alignItems="center">
            <>
              <Button onClick={handleCloseCertPopup} variant="outlined">
                Cancel
              </Button>
              {step === 2 && (isAddIssuer || isEditIssuer) ? (
                <Button
                  variant="contained"
                  color="primary"
                  style={{ marginLeft: '10px' }}
                  onClick={() => {
                    if (isEditIssuer) {
                      onEditIssuer();
                    } else {
                      onAddIssuer();
                    }
                  }}
                >
                  Add
                </Button>
              ) : (
                <Button variant="contained" color="primary" style={{ marginLeft: '10px' }} onClick={onNext}>
                  {step !== 3 ? 'Next' : 'Save'}
                </Button>
              )}
            </>
          </Grid>
        </Grid>
      </Grid>
    </Modal>
  );
}
